import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import IPage from '../../../interfaces/IPage';
import { BiAddToQueue } from 'react-icons/bi';
import CustomTableSortLabel from '../../../components/custom-table-sort-label/CustomTableSortLabel';
import CustomTablePagination from '../../../components/custom-table-pagination/CustomTablePagination';
import { useContext, useEffect, useState } from 'react';
import ICompany from '../../../interfaces/ICompany';
import { Container } from './CompaniesList.styled';
import { IFilterCompany, useCompanyService } from '../../../hooks/services/useCompanyService';
import { useToastrVanila } from '../../../hooks/util/useToastrVanila';
import { AiTwotoneEdit } from 'react-icons/ai';
import { BsTrash } from 'react-icons/bs';
import { useNavigate } from 'react-router-dom';
import FollowupSearchCompany from '../../followup/FollowupSearchCompany';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business';
import { sdsGrayscale } from '../../../theme/sds-tokens';
import { hasAccessToAction } from '../../../util/hasAccessTo';
import UserContext from '../../../context/user/UserContext';

interface CompaniesListProps extends IPage {}

const CompaniesList = (props: CompaniesListProps) => {
  window.document.title = props.title;
  const [companies, setCompanies] = useState<ICompany[] | null>(null);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [orderDirection, setOrderDirection] = useState<'asc' | 'desc'>('asc');
  const [orderField, setOrderField] = useState<string>('code');
  const [loading, setLoading] = useState(false);
  const [company, setCompany] = useState<ICompany | null>(null);

  const request = useCompanyService();
  const toastr = useToastrVanila();
  const navigate = useNavigate();

  const { state } = useContext(UserContext);
  const { resourcesWithActions } = state;
          
  const isEdit = hasAccessToAction(
    ["parametros-empresa-atestado-web"],
    resourcesWithActions,
    "Editar"
  );

  const listCompanies = async (
    perPage: number,
    currentPage: number,
    orderField: string,
    orderDirection: 'asc' | 'desc',
    filter?: IFilterCompany,
  ) => {
    setLoading(true);
    const id = filter?.id || company?.id;
    await request
      .list(perPage, currentPage + 1, orderField, orderDirection, { id })
      .then(result => {
        setCompanies([...result?.data]);
        setTotalRows(result.totalRows);
        setLoading(false);
      })
      .catch(error => {
        toastr.error(error.message);
        setLoading(false);
      });
  };

  const removeCompany = async (id: number) => {
    setLoading(true);
    await request
      .remove(id)
      .then(async result => {
        await listCompanies(perPage, currentPage, orderField, orderDirection);
        setLoading(false);
      })
      .catch(error => {
        toastr.error(error.message);
        setLoading(false);
      });
  };

  useEffect(() => {
    listCompanies(perPage, currentPage, orderField, orderDirection);
  }, [company]);

  return (
    <Container>
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
            padding: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h2" component="h2" fontWeight={600}>
              Listagem de empresas
            </Typography>
          </Box>
          <Tooltip title="Cadastrar novo" placement="left-start">
            <Button
              variant="contained"
              size="small"
              startIcon={<BiAddToQueue />}
              onClick={() => navigate(`/company/${0}/new`)}
              disabled={!isEdit}
            >
              Adicionar empresa
            </Button>
          </Tooltip>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
            paddingX: 2,
            paddingBottom: 2,
          }}
        >
          <Box sx={{ flexGrow: 1, minWidth: 240, maxWidth: 420 }}>
            <FollowupSearchCompany
              setCompany={company => {
                setCurrentPage(0);
                setCompany(company);
              }}
              company={company}
            />
          </Box>
          <Typography
            variant="labels"
            sx={{ color: theme => (theme.palette.mode === 'dark' ? sdsGrayscale[40] : sdsGrayscale[60]) }}
          >
            {`${totalRows} ${totalRows === 1 ? 'empresa' : 'empresas'}`}
          </Typography>
        </Box>

      <TableContainer>
        {loading && <LinearProgress />}
        <Table>
          <TableHead>
            <TableRow>
              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="code"
                label="Code"
                handleRequest={listCompanies}
                direction={orderDirection}
                setField={setOrderField}
                setDirection={setOrderDirection}
              />
              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="name"
                label="Nome"
                handleRequest={listCompanies}
                direction={orderDirection}
                setField={setOrderField}
                setDirection={setOrderDirection}
              />
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {companies?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ padding: 1, maxWidth: '100vw' }}
                  >
                    <SearchIcon sx={{ marginRight: 2, fontSize: '1.5rem' }} />
                    <Box sx={{ fontSize: '1.5rem' }}>Nenhum registro encontrado</Box>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              companies &&
              companies.map(company => {
                return (
                  <TableRow key={company.id} hover={true}>
                    <TableCell>{company.code}</TableCell>
                    <TableCell>{company.name}</TableCell>
                    <TableCell>
                      <Tooltip title="Editar" placement="top">
                        <IconButton onClick={() => navigate(`/company/${company.id}/edit`)} disabled={!isEdit}>
                          <AiTwotoneEdit style={{ fontSize: '15px' }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir" placement="top">
                        <IconButton onClick={() => removeCompany(company.id || 0)} disabled={!isEdit}>
                          <BsTrash style={{ fontSize: '15px' }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
        <CustomTablePagination
          totalRows={totalRows}
          currentPage={currentPage}
          perPage={perPage}
          handleRequest={listCompanies}
          setPerPage={setPerPage}
          setCurrentPage={setCurrentPage}
          orderField={orderField}
          orderDirection={orderDirection}
        />
      </Paper>
    </Container>
  );
};

export default CompaniesList;
